import {
  CommunityIdentity,
  DayOfWeek,
  DayPart,
  GenderIdentity,
  LegalSex,
  Race,
  TaskServiceType,
} from '@valerahealth/rtk-query'
import {
  BARIATRIC_ASSESSMENT,
  DischargeType,
} from 'components/onboardingForm/utils'
import { FORMATTED_PATIENT_HELP_PHONE } from 'constant'

export default {
  address1: 'Home Address Line 1',
  address2: 'Address Line 2',
  addAddress2: 'Add $t(address2)',
  dateOfBirth: 'Date Of Birth',
  invalidZipCode: '$t(zipCode) must be a 5 digit number.',
  invalidMinimumPatientAge:
    'At this time, we can only service patients 12 years of age or older.',
  daysOfTheWeek: 'Days of the Week',
  DischargeType: {
    [DischargeType.fromED]: 'From Emergency Department',
    [DischargeType.fromPsych]: 'From Inpatient Psychiatric Hospital',
  },
  PatientReferral: {
    patientRedirectMsg:
      'Requesting an appointment for yourself? <a>Switch to Patient View</a>',
  },
  PatientOnboarding: {
    referralRedirectMsg:
      'Requesting an appointment for your patient? <a>Switch to Provider View</a>',
    startTitle: 'Get Started',
    startDescription:
      'Your mental health is important and we want to match you with the most appropriate service type and provider to meet your needs. Please fill out the following information and have your insurance information ready. We look forward to working with you! If you are requesting a consultation on behalf of your patient, please use the referral form <a>here</a>. <b>Participating States: CO*, MA, NJ, NY, VT, WA</b>.',
    startDescriptionPrivacyTermsConditions:
      "By clicking next, you are consenting to Valera Health's <privacy>Privacy Policy</privacy> and <conditions>Terms & Conditions.</conditions>",
    coNote:
      '* Note for Colorado (CO): This is a special partnership between Valera Health and CCHA. Valera Health is able to serve CCHA clients in collaboration with their PCP, and match them with a mental health provider to suit their needs in a timely manner. For more information or to refer a patient to us, visit <Link>valerahealth.com/ccha</Link>.',
    endTitle: 'Thank You for Completing Your Intake Form!',
    //(without patient call in prompt, could see team ask for this again in the future)  endDescription:
    //   'A Health Connector will reach out to you within 4 business days to review this information and complete onboarding. To learn more about what to expect, visit our How It Works page.',
    endDescription:
      'Looking to speak to a Health Connector today? Call the number below now!',
    holidayWarning:
      'Please note we are working with reduced staff <em>between the dates of 12/26/23 and 1/1/24</em> for the Holidays. We will respond as quickly as possible.',
    endCTA: 'How It Works',
    finalizeDescription:
      'We will make every effort to contact you during your preferred time window(s). Please select your time preference(s) below.',
    phoneVerificationBody: 'Please enter the code sent to {{number}}.',
    codeValueRequired: 'The verification code is required to continue.',
    resendVerificationCode: "Didn't receive your code? <i>resend code</i>",
    resendVerificationCodeHlpTxt: `If you're having trouble receiving your code, call <link>${FORMATTED_PATIENT_HELP_PHONE}</link>.`,
    resendOTPSuccess: 'A new code has been sent to {{number}}',
    sendOTPFailure: 'Failed to send a code to {{number}}',
    confirmOTPFailure: 'The code you entered is not valid',
    requestLocked:
      'This action cannot be completed due to a temporary block on this number from recent volume of sign in attempts. Please try again later.',
    confirmOTPSuccess: 'Phone number verified',
    sessionHasExpired: 'Your session has expired. Please start again.',
    otpCodeExpirationCountdown: 'This code will expire in {{duration}}',
    otpCodeExpired:
      'The code has expired. Please request a new code by clicking "Resend Code" above.',
    otpUnsubscribed:
      'Your phone number is currently unsubscribed from SMS verification.',
    otpResubscribeInstructions:
      'Please text "unstop" or "start" to {{contactNumber}} and then try again.',
    specialty: 'What brings you here today?',
    serviceType: 'What type of care are you seeking?',
    selectAllThatApply: 'Select all that apply',
    tellUsMore: 'Tell us more',
    tellUsMorePlaceholder: "I'm looking for...",
    hospitalized:
      'Have you been hospitalized for reasons related to your mental health within the past 30 days?',
    oneOrMoreValuesAreRequired: 'Please select one or more values.',
    radioButtonRequired: 'Please select a value.',
    selfPay: "I'm not using insurance",
    selfPayTerms:
      'You acknowledge and agree that you will self-pay (pay out of pocket) for any billable services rendered by Valera Health, including any applicable no-show fees.',
    outOfNetworkInsurancePlan: 'Insurance Plan - Other',
    insuranceValidMsg:
      'Please select an Insurance Plan from the list or or choose $t(other)',
    outOfNetworkInsuranceValidMsg:
      'If you\'re not using insurance, please check the "$t(PatientOnboarding.selfPay)" checkbox above.',
    secondaryInsuranceTitle: 'Secondary Insurance',
    relationshipToInsured: 'Relationship to Insured',
    legalSex: 'Legal Sex',
    insNameHeading: 'Name of Insured',
    insNameFirst: '$t(firstName)',
    insNameMiddle: 'M.I.',
    insNameLast: '$t(lastName)',
    insSexOnFile: 'Sex on File of Insured',
    insDOB: 'Date of Birth of Insured',
    insCardUpload: 'Upload Insurance Card',
    guardianTitle: 'Parent/Guardian',
    guardianDescription:
      "State laws require a parent or guardian to be present during the onboarding process for patients considered minors (age varies by state). Please enter the parent or guardian's phone number and email address below.",
    identificationHlpTxt:
      'Please upload a government-issued ID. This is optional, but recommended for verifying your identity when matched to Valera services.',
    referralSource: 'How did you hear about us?',
    referralSourceOther: 'Referral Source - Other',
    agreementCheckboxDescription:
      "By checking this box and continuing this submission, you have reviewed and agreed to <Link>Valera Health's service agreement.</Link>",
    agreementCheckboxValidMsg:
      "Please open Valera Health's service agreement before checking the box above.",
    isThisAMedicaidPlan: 'Check here if this is a Medicaid plan.',
    notAcceptingOOOMedicaidPlans: `Unfortunately, we don't accept out-of-network Medicaid plans. Please reach out to your health plan or referring provider for additional resources`,
    // new
    referrerInformationNavTitle: 'Referrer Information',
    patientInformationNavTitle: 'Get Started',
    serviceMatchingNavTitle: 'Preferences',
    insuranceNavTitle: 'Insurance',
    finalizeNavTitle: 'Finalize',
    appointmentNavTitle: 'Appointment',
    reviewNavTitle: ' Review',
    patientInformationTitle: "Let's Get Started",
    patientVerificationTitle: 'Verify Your Phone Number',
    serviceMatchingTitle: 'How Can We Help?',
    insuranceTitle: 'Insurance',
    insuranceVerificationTitle: 'Verify Your Insurance(s)',
    appointmentTitle: 'Book Appointment',
    reviewTitle: ' Review',
    createAppointmentFailure:
      'Sorry the appointment time is no longer available. Please go back and select a different time.',
    referralServiceTypeSelection:
      'Which service would you like to refer your patient to?',
    referralServiceTypeSelectionHelper:
      'If you are not sure, we recommend starting with Therapy.',
    referralServiceTypeOptions: {
      [TaskServiceType.GroupTherapy]: 'Group Therapy',
      [TaskServiceType.MedMgmt]: 'Psychiatry / Medication Management',
      [TaskServiceType.Therapy]: 'Individual Therapy',
    },
    consentAgreementTitle: 'Consent Form',
    consentPreFormNotice:
      'Before scheduling your initial appointment with Valera Health, you will need to sign a consent to treatment form. You will be redirected to an external website to sign the form. Once completed, you can proceed to scheduling.',
    consentFormSignedNotice:
      'We have your signed consent on file. Please proceed to scheduling.',
    consentFormErrorNotice:
      'There are network issues with our consent form. Valera Health will send it before your initial appointment. Please proceed to scheduling.',
    createConsentError:
      'There are network issues with our consent form. Please try again.',
    consentLoading: 'Loading...',
    hospitalizedDescription:
      'Please call our office <Tel>{{number}}</Tel> to schedule your appointment.',
    medMgmtDescription:
      'Psychiatry/Medication Management involves working with a Board Certified Psychiatric Prescriber to review your medical history and symptoms. The provider will then determine if medication is appropriate to support your diagnosis and establish a medication regimen.',
    therapyDescription:
      'Therapy (talk therapy/psychotherapy) involves talking with a licensed clinician to discuss your thoughts, feelings, and behaviors, working to modify thought and behavior patterns. Therapy is a supportive tool for individuals to learn coping skills, process emotional challenges, and improve communication.',
    availabilityPreferences: 'Availability Preferences',
    providerPreferences: 'Provider Preferences',
  },
  PatientFiles: {
    notAuthorized:
      'This link is no longer valid, please contact the issuer for a new upload link.',
  },
  TaskServiceType: {
    CASE_MGMT: 'Case Mgmt',
    [TaskServiceType.GroupTherapy]: 'Group Therapy',
    [TaskServiceType.MedMgmt]: 'Psychiatry / Medication Management',
    [TaskServiceType.Therapy]: 'Therapy',
  },
  PatientFilesForm: {
    submitSuccess: 'The form was successfully submitted.',
    submitFailure:
      'There was a problem saving the form. If the problem persists, contact your care team members.',
  },
  newPatientInquiries: 'New Patient Inquiries',
  generalInquiries: 'General Inquiries',
  identificationCard: 'Upload Government Issued I.D.',
  identificationHlpTxt: 'Drivers License, State ID, or Passport',
  cardFront: 'Front of Card',
  cardBack: 'Back of Card',
  secondaryCardFront: 'Secondary: $t(cardFront)',
  secondaryCardBack: 'Secondary: $t(cardBack)',
  tirtiaryCardFront: 'Third: $t(cardFront)',
  tirtiaryCardBack: 'Third: $t(cardBack)',
  otherFormsOfInsurance: 'Other Forms of Insurance',
  preferredLanguage: 'Preferred Language',
  insuranceIdNumber: 'Insurance ID Number',
  insuranceIdHlpTxt:
    'This can sometimes be referred to as Insurance Member ID, Subscriber ID, or Policy ID.',
  insuranceIdValidation: 'No special characters, only numbers and letters',
  insuranceFECANumber: 'Insurance Policy Group or FECA Number',
  patientStateValidation:
    'Valera Health only practices in the following states at this time: {{stateCodes, list}}',
  imgHelperText:
    'Please take pictures in a well-lit area, making sure the information is readable.',
  hasSecondaryInsurance:
    'Do you have any secondary form of insurance, including Medicaid?',
  hasTertiaryInsurance:
    'Do you have any other form of insurance, including Medicaid?',
  patientGenderIdentityOther: 'Gender - Other',
  returnToMobileApp: 'Return to the Valera Health Mobile App',
  phone_legal_claim:
    'By providing my phone number, I authorize Valera Health and its affiliates to contact me by calling or by automated SMS text message.',
  optional: 'Optional',
  payer: 'Insurance Company',
  primaryInsurance: 'Primary Insurance',
  secondaryInsurance: 'Secondary Insurance',
  tertiaryInsurance: 'Tertiary Insurance',
  verify: 'Verify',
  active: 'Active',
  unableToVerify: 'Unable to verify',
  verifyPending: 'Verification pending',
  verifying: 'Verifying',
  coPay: 'Co-Pay',
  coInsurance: 'Coinsurance',
  deductible: 'Deductible',
  InsuranceVerification: {
    coPayTooltip:
      "Co-Pay is a fixed amount ($20, for example) you pay for a covered health care service after you've paid your deductible.",
    coInsuranceTooltip:
      "Coinsurance is the percentage of costs of a covered health care service you pay (20%, for example) after you've paid your deductible.",
    deductibleTooltip:
      'Deductible is the amount you pay for covered health care services before your insurance plan starts to pay. With a $2,000 deductible, for example, you pay the first $2,000 of covered services yourself.',
    highDeductibleWarning:
      'Please note that this is a high deductible health plan.',
    activeInfo:
      'This is an estimate of your telemedicine benefits as returned by your insurance company. Final patient responsibility is dependent on the terms of your insurance plan and how your insurance processes your claim. Please speak with your plan directly to confirm these benefits.',
    failInfo:
      'Please review your information above. If you believe this information is correct, please continue and we will verify your insurance manually.',
    failInfoPastConfirmation:
      "We'll need more information to manually verify your insurance. Please complete the following and you'll hear from us soon.",
    failConfirmation: 'I verify this information is correct.',
    pendingInfo:
      "We accept this insurance, but need to manually verify it. Please complete the following and you'll hear from us soon.",
    pendingInfoOON:
      "This health plan isn't returning sufficient information to determine eligibility. We'll need more time to verify your benefits. We'll reach out to you as soon as we have more information. Please click <Link>here</Link> to learn more about our self-pay options.",
    insuranceVerificationErrorMsg:
      'Fail to verify insurance due to server error',
  },
  // new
  providerName: 'Search Provider',
  nextAvailable: 'Next Available',
  insuranceAccepted: 'Insurance Accepted',
  treats: 'Treats',
  bio: 'Bio',
  identities: 'Identities',
  expertise: 'Expertise',
  treatmentMethods: 'Treatment Methods',
  pickADate: 'Pick A Date',
  pickATime: 'Pick A Time',
  providerPanelHeader:
    'There are <strong>{{number}}</strong> providers that match your criteria.',
  providerPanelHeader1:
    'There is <strong>1</strong> provider that matches your criteria.',
  joinWaitList: 'Please let us know your availability.',
  noAppointment:
    "We couldn't find any appointment time slot under the current provider filters.",
  forceWaitList: 'None of the time slots work for me.',
  patientPreferenceDuration: 'Minute Appointment',
  durationLongVersion: 'How long would you like your appointment to take?',
  dayOfWeekLongVersion: 'Which day(s) usually work best for you?',
  dayPartLongVersion: 'What time of the day usually work best for you?',
  timeLongVersion: 'What time(s) usually work best for you?',
  appointmentDuration: 'Appointment Duration',
  appointmentBrief: '{{serviceType}} appt. with {{name}}',
  virtualSession: 'Virtual Session',
  min: 'Min',
  countDownMsg:
    "We will reserve the appointment for you for {{min}} minutes while you're reviewing the booking information.",
  waitListMsg:
    'No appointment has been made yet. Our staff will connect you with a provider who meets the criteria.',
  identity: 'Identity',
  selectedAppoitmentSlot: 'Selected',
  submit: 'submit',
  finishAndSubmit: 'Finish and Submit',
  bookAppointment: 'Book Appointment',
  tryAgain: 'Please Try Again',
  filters: 'Provider Filters',
  additionalFilters: 'Additional Preferences',
  referralSource: 'Referral Source',
  preferences: 'Preferences',
  appliedFilters: 'Applied Provider Filters',
  filterOptions: 'Options',
  addOptionsText: 'Click on the chip to select your preference.',
  review: 'Review',
  Identity: {
    [LegalSex.Male]: 'Man',
    [LegalSex.Female]: 'Woman',
    [Race.Native]: 'American Indian or Alaska Native',
    [Race.Asian]: 'Asian',
    [Race.Black]: 'Black or African American',
    [Race.PacificIslander]: 'Native Hawaiian or Other Pacific Islander',
    [Race.White]: 'White',
    [CommunityIdentity.Bipoc]: 'BIPOC',
    [CommunityIdentity.Lgbtqia]: 'LGBTQIA+',
  },
  DayPart: {
    [DayPart.morning]: 'Morning (8AM - 12PM)',
    [DayPart.afternoon]: 'Afternoon (12PM - 4PM)',
    [DayPart.evening]: 'Evening (4PM - 8PM)',
  },
  DayOfWeek: {
    [DayOfWeek.Monday]: 'Monday',
    [DayOfWeek.Tuesday]: 'Tuesday',
    [DayOfWeek.Wednesday]: 'Wednesday',
    [DayOfWeek.Thursday]: 'Thursday',
    [DayOfWeek.Friday]: 'Friday',
    [DayOfWeek.Saturday]: 'Saturday',
    [DayOfWeek.Sunday]: 'Sunday',
  },
  Language: {
    'American Sign Language (ASL)': 'American Sign Language (ASL)',
    Arabic: 'Arabic',
    Bengali: 'Bengali',
    Cantonese: 'Cantonese',
    Chinese: 'Chinese',
    'Chinese (China)': 'Chinese (China)',
    'Chinese (Hong Kong)': 'Chinese (Hong Kong)',
    'Chinese (Singapore)': 'Chinese (Singapore)',
    'Chinese (Taiwan)': 'Chinese (Taiwan)',
    Creole: 'Creole',
    Croatian: 'Croatian',
    Czech: 'Czech',
    Danish: 'Danish',
    Dutch: 'Dutch',
    'Dutch (Belgium)': 'Dutch (Belgium)',
    'Dutch (Netherlands)': 'Dutch (Netherlands)',
    English: 'English',
    'English (Australia)': 'English (Australia)',
    'English (Canada)': 'English (Canada)',
    'English (Great Britain)': 'English (Great Britain)',
    'English (India)': 'English (India)',
    'English (New Zealand)': 'English (New Zealand)',
    'English (Singapore)': 'English (Singapore)',
    'English (United States)': 'English (United States)',
    Finnish: 'Finnish',
    French: 'French',
    'French (Belgium)': 'French (Belgium)',
    'French (Canada)': 'French (Canada)',
    'French (France)': 'French (France)',
    'French (Switzerland)': 'French (Switzerland)',
    Frysian: 'Frysian',
    'Frysian (Netherlands)': 'Frysian (Netherlands)',
    German: 'German',
    'German (Austria)': 'German (Austria)',
    'German (Germany)': 'German (Germany)',
    'German (Switzerland)': 'German (Switzerland)',
    Greek: 'Greek',
    Guyanese: 'Guyanese',
    'Haitian Creole': 'Haitian Creole',
    Hebrew: 'Hebrew',
    Hindi: 'Hindi',
    Italian: 'Italian',
    'Italian (Italy)': 'Italian (Italy)',
    'Italian (Switzerland)': 'Italian (Switzerland)',
    Japanese: 'Japanese',
    Khmer: 'Khmer',
    Korean: 'Korean',
    Mandarin: 'Mandarin',
    Norwegian: 'Norwegian',
    'Norwegian (Norway)': 'Norwegian (Norway)',
    Patois: 'Patois',
    Polish: 'Polish',
    Portuguese: 'Portuguese',
    'Portuguese (Brazil)': 'Portuguese (Brazil)',
    Punjabi: 'Punjabi',
    Romanian: 'Romanian',
    Russian: 'Russian',
    'Russian (Russia)': 'Russian (Russia)',
    Serbian: 'Serbian',
    'Serbian (Serbia)': 'Serbian (Serbia)',
    Spanish: 'Spanish',
    'Spanish (Argentina)': 'Spanish (Argentina)',
    'Spanish (Spain)': 'Spanish (Spain)',
    'Spanish (Uruguay)': 'Spanish (Uruguay)',
    Swahili: 'Swahili',
    Swedish: 'Swedish',
    'Swedish (Sweden)': 'Swedish (Sweden)',
    Telegu: 'Telegu',
    Turkish: 'Turkish',
    Urdu: 'Urdu',
    Vietnamese: 'Vietnamese',
    Yiddish: 'Yiddish',
    Yoruba: 'Yoruba',
  },
  Specialty: {
    Anxiety: 'Anxiety',
    'LGTBQ+ centered care': 'LGTBQ+ centered care',
    Medications: 'Medications',
    'Pregnancy/Post-Partum': 'Pregnancy/Post-Partum',
    Relationships: 'Relationships',
    Sadness: 'Sadness',
    'Schizophrenia or bipolar disorder': 'Schizophrenia or bipolar disorder',
    Stress: 'Stress',
    'Work Stress': 'Work Stress',
    'I just want to talk to someone': 'I just want to talk to someone',
    'Substance Misuse': 'Substance Misuse',
    'Lack of Motivation': 'Lack of Motivation',
    'Family Issues': 'Family Issues',
    'Body Image': 'Body Image',
    Nervousness: 'Nervousness',
    'Grief / Loss': 'Grief / Loss',
    'Anger Management': 'Anger Management',
    [BARIATRIC_ASSESSMENT]: 'Bariatric Assessment',
    Other: 'Other',
  },
  Modalities: {
    'Acceptance & Commitment Therapy': 'Acceptance & Commitment Therapy',
    'Anger Management': 'Anger Management',
    CBT: 'CBT',
    Coaching: 'Coaching',
    'Cognitive Behavioral Therapy Informed':
      'Cognitive Behavioral Therapy Informed',
    'Couples Therapy': 'Couples Therapy',
    'Crisis Intervention/CISM': 'Crisis Intervention/CISM',
    DBT: 'DBT',
    EMDR: 'EMDR',
    'Exposure Therapy': 'Exposure Therapy',
    'Gentle Reprocessing': 'Gentle Reprocessing',
    'Group Therapy': 'Group Therapy',
    'Hypnosis/Hypnotherapy': 'Hypnosis/Hypnotherapy',
    'Individual Therapy': 'Individual Therapy',
    'Medication Management (Prescribers Only)':
      'Medication Management (Prescribers Only)',
    'Mindfulness Based Therapy': 'Mindfulness Based Therapy',
    'Motivational Interviewing': 'Motivational Interviewing',
    'N/A Valera Corporate': 'N/A Valera Corporate',
    'Play Therapy': 'Play Therapy',
    'PMT - Parent Management Training': 'PMT - Parent Management Training',
    'Positive Psychology': 'Positive Psychology',
    'Psychodynamic Therapy': 'Psychodynamic Therapy',
    'Solution Focused Therapy & Problem-Solving Treatment':
      'Solution Focused Therapy & Problem-Solving Treatment',
    'Somatic Experiencing': 'Somatic Experiencing',
    'Somatic Therapy': 'Somatic Therapy',
    'Supportive Therapy': 'Supportive Therapy',
    'Trauma Focused - Cognitive Behavioral Therapy (TF-CBT)':
      'Trauma Focused - Cognitive Behavioral Therapy (TF-CBT)',
    'Trauma - Informed': 'Trauma - Informed',
    'Triple P - Positive Parenting Program':
      'Triple P - Positive Parenting Program',
  },
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
  dayOfWeek: 'Day of Week',
  dayPart: 'Time of Day',
  serviceTypeHelperText:
    'Not sure where to start? Many people begin their mental health journey with <strong>Therapy</strong>.',
  race: 'Race',
  communityIdentity: 'Community Identity',
  secondaryLanguage: 'Secondary Language',
  inactiveCoverage: 'Inactive Coverage',
  timeslotHelperTextForBariatric:
    "To support your Bariatric Assessment needs, we've included both psychiatrists and therapists who may be a good fit.",
}
